import { CVBuilderFormValues } from 'utils/cv-form-db';
import {
  CefrLevelApi,
  CreateCvRequest,
  CvDto,
  EducationEntryRequest,
  ExperienceEntryRequest,
  HardSkillEntryRequest,
  InsuranceTypeApi,
  LanguageSkillEntryRequest,
  PersonalDetailsRequest,
  SkillLevelApi
} from './api-types';

const toInsuranceType = (value: string | undefined): InsuranceTypeApi => {
  const upper = (value ?? '').toUpperCase() as InsuranceTypeApi;
  if (upper === InsuranceTypeApi.PRIVATE) {
    return InsuranceTypeApi.PRIVATE;
  }
  return InsuranceTypeApi.STATUTORY;
};

const toSkillLevel = (value: string | undefined): SkillLevelApi => {
  switch ((value ?? '').toUpperCase()) {
    case SkillLevelApi.ADVANCED:
      return SkillLevelApi.ADVANCED;
    case SkillLevelApi.INTERMEDIATE:
      return SkillLevelApi.INTERMEDIATE;
    default:
      return SkillLevelApi.BEGINNER;
  }
};

const toCefrLevel = (value: string | undefined): CefrLevelApi => {
  const upper = (value ?? '').toUpperCase() as CefrLevelApi;
  if (
    upper === CefrLevelApi.A1 ||
    upper === CefrLevelApi.A2 ||
    upper === CefrLevelApi.B1 ||
    upper === CefrLevelApi.B2 ||
    upper === CefrLevelApi.C1 ||
    upper === CefrLevelApi.C2
  ) {
    return upper;
  }
  return CefrLevelApi.A1;
};

const toPersonalDetails = (
  values: CVBuilderFormValues['personalDetails']
): PersonalDetailsRequest => ({
  firstname: values.firstname ?? '',
  lastname: values.lastname ?? '',
  email: values.email ?? '',
  phone: values.phone ?? '',
  zip: values.zip ?? '',
  insuranceType: toInsuranceType(values.insuranceType),
  insuranceName: values.insuranceName ?? '',
  weeklyAvailableHours: Number(values.weeklyAvailableHours) || 0,
  stipendBafoeg: values.stipendBafoeg === 'true'
});

const toEducation = (
  entries: CVBuilderFormValues['education']
): EducationEntryRequest[] =>
  entries.map(entry => ({
    university: entry.university,
    study: entry.study,
    degree: entry.degree,
    currentlyEnrolled: !!entry.currentlyEnrolled,
    studyStart: entry.studyStart,
    studyEnd: entry.currentlyEnrolled ? undefined : entry.studyEnd
  }));

const toExperience = (
  entries: CVBuilderFormValues['professionalExperience']
): ExperienceEntryRequest[] =>
  entries.map(entry => ({
    companyName: entry.companyName,
    positionTitle: entry.position,
    startDate: entry.startDate,
    endDate: entry.currentlyWorking ? undefined : entry.endDate,
    currentlyWorking: !!entry.currentlyWorking,
    description: entry.description
  }));

const toHardSkills = (
  entries: CVBuilderFormValues['hardSkills']
): HardSkillEntryRequest[] =>
  entries.map(entry => ({
    name: entry.name ?? '',
    level: toSkillLevel(entry.level),
    professionallyUsed: !!entry.professionallyUsed
  }));

const toLanguageSkills = (
  entries: CVBuilderFormValues['languageSkills']
): LanguageSkillEntryRequest[] =>
  entries.map(entry => ({
    language: entry.language ?? '',
    level: toCefrLevel(entry.level)
  }));

export const toCreateCvRequest = (
  values: CVBuilderFormValues
): CreateCvRequest => ({
  language: values.language,
  personalDetails: toPersonalDetails(values.personalDetails),
  education: toEducation(values.education),
  professionalExperience: toExperience(values.professionalExperience),
  hardSkills: toHardSkills(values.hardSkills),
  softSkills: values.softSkills,
  languageSkills: toLanguageSkills(values.languageSkills),
  certificates: values.certificates
});

// The redeem/read response can OMIT empty collections and an all-empty personalDetails
// (backend Jackson `non_empty` policy), so every field below may be undefined on the wire
// — for a partial guest draft OR a complete CV with no optional experience/skills. We
// default each to a safe empty value so resuming/hydrating a sparse CV never crashes
// (was: "Cannot read properties of undefined (reading 'map')"). The backend also serializes
// these as [] now (CvHttp @JsonInclude(NON_NULL)); this is the matching client-side guard.
export const fromCvDto = (dto: CvDto): Partial<CVBuilderFormValues> => {
  const pd = dto.personalDetails;
  return {
    language: dto.language,
    // Only override personalDetails when the response actually carries it. `non_empty`
    // drops an all-empty personalDetails object from the wire; in that case we must keep
    // the form defaults (blank strings, unselected stipendBafoeg) rather than overwrite
    // them with `undefined` fields / a forced 'false' (which would pre-select "No").
    ...(pd
      ? {
          personalDetails: {
            firstname: pd.firstname,
            lastname: pd.lastname,
            email: pd.email,
            phone: pd.phone,
            zip: pd.zip,
            // Keep the canonical uppercase enum (STATUTORY/PRIVATE) the backend
            // serves and the Select options use. Lowercasing here was breaking the
            // Select match (value stayed unselected) and the /data/healthInsurances
            // lookup (type=statutory -> 400; the validator expects STATUTORY|PRIVATE).
            insuranceType: pd.insuranceType?.toUpperCase(),
            insuranceName: pd.insuranceName,
            weeklyAvailableHours: pd.weeklyAvailableHours,
            stipendBafoeg: pd.stipendBafoeg ? 'true' : 'false'
          }
        }
      : {}),
    education: (dto.education ?? []).map(e => ({
      university: e.university,
      study: e.study,
      degree: e.degree,
      currentlyEnrolled: e.currentlyEnrolled,
      studyStart: e.studyStart,
      studyEnd: e.studyEnd
    })),
    professionalExperience: (dto.professionalExperience ?? []).map(e => ({
      companyName: e.companyName,
      position: e.positionTitle,
      startDate: e.startDate,
      endDate: e.endDate,
      currentlyWorking: e.currentlyWorking,
      description: e.description
    })),
    hardSkills: (dto.hardSkills ?? []).map(s => ({
      name: s.name,
      level: s.level?.toLowerCase(),
      professionallyUsed: s.professionallyUsed
    })),
    softSkills: dto.softSkills ?? [],
    languageSkills: (dto.languageSkills ?? []).map(l => ({
      language: l.language,
      level: l.level
    })),
    certificates: dto.certificates ?? []
  };
};
